import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'

export interface TimeRemainingView {
  figure: number
  label: string
}

@Injectable({
  providedIn: 'root',
})
export class GbTimerHelper {
  private isTimerEnding$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private hasTimerEnded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  days = 0
  hours = 0
  minutes = 0
  seconds = 0
  warningSeconds = 0
  pluralUnits = false

  private unitLabelMap = {
    days: (plural: boolean) => (plural ? 'TIMER.DAYS' : 'TIMER.DAY'),
    hours: (plural: boolean) => (plural ? 'TIMER.HOURS' : 'TIMER.HOUR'),
    minutes: (plural: boolean) => (plural ? 'TIMER.MINUTES' : 'TIMER.MINUTE'),
    seconds: (plural: boolean) => (plural ? 'TIMER.SECONDS' : 'TIMER.SECOND'),
  }

  setWarningSeconds(warningSeconds: number): void {
    this.warningSeconds = warningSeconds
  }

  getIsTimerEnding(): Observable<boolean> {
    return this.isTimerEnding$.asObservable()
  }

  getHasTimerEnded(): Observable<boolean> {
    return this.hasTimerEnded$.asObservable()
  }

  getTimeRemaining(endDate: Date = new Date(), pluralUnits: boolean = false): TimeRemainingView[] {
    let arr: TimeRemainingView[]
    // Time calculations for days, hours, minutes and seconds
    this.initTimeCalculations(endDate)
    // Set if timer is ending
    this.isTimerEnding$.next(this.isCurrentTimeLessOrEqualWarning())
    // Set if timer has ended
    this.hasTimerEnded$.next(this.hasTimerEnded())
    // Set if time units should be plural when one remaining i.e. 1 day/s
    this.pluralUnits = pluralUnits

    if (this.hours === 0 && this.days === 0) {
      if (!pluralUnits && this.minutes === 0) {
        // Display seconds if less than one minute to end time
        arr = this.createTimeRemainingView(['seconds'])
      } else {
        // Display minutes & seconds if less than one hour to end time
        arr = this.createTimeRemainingView(['minutes', 'seconds'])
      }
    } else if (this.days === 0) {
      // Display hours & minutes if less than one day to end time
      arr = this.createTimeRemainingView(['hours', 'minutes'])
    } else {
      // Display days & hours if more than one day to end time
      arr = this.createTimeRemainingView(['days', 'hours'])
    }
    return arr
  }

  private initTimeCalculations(endDate: Date): void {
    // Get the difference between now and timer end date
    const timeDifference = new Date(endDate).getTime() - new Date().getTime()

    if (timeDifference > 0) {
      this.days = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
      this.hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      this.minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60))
      this.seconds = Math.floor((timeDifference % (1000 * 60)) / 1000)
    } else {
      this.days = this.hours = this.minutes = this.seconds = 0
    }
  }

  private createTimeRemainingView(units: string[]): TimeRemainingView[] {
    const figureObjMap: { [k: string]: any } = {
      days: { figure: this.days },
      hours: { figure: this.hours },
      minutes: { figure: this.minutes },
      seconds: { figure: this.seconds },
    }

    return units.map((unit: string) => {
      const key = unit as keyof typeof figureObjMap
      figureObjMap[key].label = this.getLabel(unit, figureObjMap[key].figure)
      return figureObjMap[key]
    })
  }

  private getLabel(unit: string, timeRemaining: number): string {
    const key = unit as keyof typeof this.unitLabelMap
    return this.unitLabelMap[key](this.shouldPluralUnits(timeRemaining))
  }

  private shouldPluralUnits(timeRemaining: number): boolean {
    return this.pluralUnits ? true : timeRemaining > 1
  }

  private isCurrentTimeLessOrEqualWarning(): boolean {
    return [this.days, this.hours, this.minutes].every((item) => item === 0) && this.seconds <= this.warningSeconds
  }

  private hasTimerEnded(): boolean {
    return [this.days, this.hours, this.minutes, this.seconds].every((item) => item === 0)
  }
}
